import { useEffect, useState, useCallback, useMemo } from 'react';
import { apiFetch } from '@/lib/api';
import { Button } from '@/components/ui/button';
import { Card, CardContent } from '@/components/ui/card';
import { Label } from '@/components/ui/label';
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '@/components/ui/select';
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from '@/components/ui/table';
import { SummaryMetricCard, MetricGrid } from '@/components/global/SummaryMetricCard';
import { PoundSterling, Warehouse, Cpu, Layers, Download } from 'lucide-react';
import { cn } from '@/lib/utils';
import { LocationLink } from '@/components/locations';
import { downloadCsv } from '@/lib/csvExport';
import { machineOptionLabel } from '@/lib/machineLabel';
import { parseMachineSelectOptions } from '@/lib/machineCatalog';

type StockValueLocationType = 'HUB' | 'SPOKE' | 'MACHINE';
type ValueView = 'cost' | 'retail';

interface StockValueItem {
  locationType: StockValueLocationType;
  locationId: string;
  locationName: string;
  productCount: number;
  totalUnits: number;
  costValue: number;
  retailValue: number;
  totalValue: number;
  averageCostPerUnit: number;
  averageRetailPerUnit: number;
}

interface StockValueReport {
  view: ValueView;
  items: StockValueItem[];
  summary: {
    totalLocations: number;
    totalProducts: number;
    totalUnits: number;
    warehouseCostValue: number;
    warehouseRetailValue: number;
    machinesCostValue: number;
    machinesRetailValue: number;
    totalCostValue: number;
    totalRetailValue: number;
    totalValue: number;
  };
}

interface CategoryOption {
  id: string;
  name: string;
}

interface BrandOption {
  id: string;
  name: string;
}

interface LocationOption {
  id: string;
  name: string;
  type: StockValueLocationType;
}

function formatCurrency(value: number): string {
  return new Intl.NumberFormat('en-GB', {
    style: 'currency',
    currency: 'GBP',
    minimumFractionDigits: 2,
    maximumFractionDigits: 2,
  }).format(value);
}

function buildQueryParams(filters: {
  view: ValueView;
  locationType: string;
  locationId: string;
  categoryId: string;
  brandId: string;
}): string {
  const params = new URLSearchParams();
  params.set('view', filters.view);
  if (filters.locationType !== 'all') params.set('locationType', filters.locationType);
  if (filters.locationId !== 'all') params.set('locationId', filters.locationId);
  if (filters.categoryId !== 'all') params.set('categoryId', filters.categoryId);
  if (filters.brandId !== 'all') params.set('brandId', filters.brandId);
  return params.toString();
}

export default function StockValueReportPage() {
  const [report, setReport] = useState<StockValueReport | null>(null);
  const [loading, setLoading] = useState(true);
  const [view, setView] = useState<ValueView>('retail');
  const [locationType, setLocationType] = useState('all');
  const [locationId, setLocationId] = useState('all');
  const [categoryId, setCategoryId] = useState('all');
  const [brandId, setBrandId] = useState('all');
  const [categories, setCategories] = useState<CategoryOption[]>([]);
  const [brands, setBrands] = useState<BrandOption[]>([]);
  const [locations, setLocations] = useState<LocationOption[]>([]);

  useEffect(() => {
    Promise.all([
      apiFetch('/api/v1/categories'),
      apiFetch('/api/v1/brands'),
      apiFetch('/api/v1/warehouses?pageSize=500'),
      apiFetch('/api/v1/machines?pageSize=500'),
    ]).then(async ([catRes, brandRes, whRes, mcRes]) => {
      if (catRes.ok) {
        const data = await catRes.json();
        setCategories(Array.isArray(data) ? data : data.items ?? []);
      }
      if (brandRes.ok) {
        const data = await brandRes.json();
        setBrands(Array.isArray(data) ? data : data.items ?? []);
      }
      const locs: LocationOption[] = [];
      if (whRes.ok) {
        const data = await whRes.json();
        const warehouses = data.items ?? data ?? [];
        for (const wh of warehouses) {
          if (!wh?.id) continue;
          locs.push({
            id: wh.id,
            name: wh.name?.trim() || wh.code?.trim() || 'Unnamed warehouse',
            type: wh.type === 'HUB' || wh.type === 'SPOKE' ? wh.type : 'SPOKE',
          });
        }
      }
      if (mcRes.ok) {
        const data = await mcRes.json();
        for (const machine of parseMachineSelectOptions(data)) {
          locs.push({
            id: machine.id,
            name: machineOptionLabel(machine),
            type: 'MACHINE',
          });
        }
      }
      locs.sort((a, b) => a.name.localeCompare(b.name, undefined, { sensitivity: 'base' }));
      setLocations(locs);
    });
  }, []);

  const filteredLocationOptions = useMemo(() => {
    if (locationType === 'all') return locations;
    return locations.filter((l) => l.type === locationType);
  }, [locations, locationType]);

  useEffect(() => {
    if (locationId !== 'all' && !filteredLocationOptions.some((l) => l.id === locationId)) {
      setLocationId('all');
    }
  }, [locationId, filteredLocationOptions]);

  const loadData = useCallback(async () => {
    setLoading(true);
    try {
      const qs = buildQueryParams({ view, locationType, locationId, categoryId, brandId });
      const res = await apiFetch(`/api/v1/reports/stock-value?${qs}`);
      if (res.ok) {
        const data = await res.json();
        setReport(data);
      }
    } catch (error) {
      console.error('Error loading stock value report:', error);
    } finally {
      setLoading(false);
    }
  }, [view, locationType, locationId, categoryId, brandId]);

  useEffect(() => {
    loadData();
  }, [loadData]);

  const summaryValue = (cost: number, retail: number) =>
    view === 'retail' ? formatCurrency(retail) : formatCurrency(cost);

  function exportCsv() {
    if (!report || report.items.length === 0) return;
    // The rows already reflect the active filters — they are what the server
    // returned for the current query.
    downloadCsv(
      `stock-value-${new Date().toISOString().slice(0, 10)}.csv`,
      ['Location', 'Type', 'SKUs', 'Units', 'Cost value (GBP)', 'Retail value (GBP)'],
      report.items.map((item) => [
        item.locationName,
        item.locationType,
        item.productCount,
        item.totalUnits,
        item.costValue.toFixed(2),
        item.retailValue.toFixed(2),
      ])
    );
  }

  return (
    <div className="min-h-screen bg-muted/30">
      <div className="space-y-6">
        <div className="flex flex-col gap-4 sm:flex-row sm:items-center sm:justify-between">
          <h1 className="text-2xl font-semibold">Stock Valuation</h1>
          <div className="flex items-center gap-2">
            <div className="inline-flex rounded-md border bg-background p-0.5">
              <Button
                type="button"
                size="sm"
                variant={view === 'retail' ? 'default' : 'ghost'}
                className="h-8"
                onClick={() => setView('retail')}
              >
                Retail view
              </Button>
              <Button
                type="button"
                size="sm"
                variant={view === 'cost' ? 'default' : 'ghost'}
                className="h-8"
                onClick={() => setView('cost')}
              >
                Cost view
              </Button>
            </div>
            <Button
              variant="outline"
              onClick={exportCsv}
              disabled={loading || !report || report.items.length === 0}
            >
              <Download className="h-4 w-4 mr-2" />
              Export
            </Button>
          </div>
        </div>

        <Card>
          <CardContent className="pt-6">
            <div className="grid gap-4 sm:grid-cols-2 lg:grid-cols-4">
              <div className="space-y-2">
                <Label>Location</Label>
                <Select value={locationId} onValueChange={setLocationId}>
                  <SelectTrigger>
                    <SelectValue placeholder="All locations" />
                  </SelectTrigger>
                  <SelectContent>
                    <SelectItem value="all">All locations</SelectItem>
                    {filteredLocationOptions.map((loc) => (
                      <SelectItem key={loc.id} value={loc.id}>
                        {loc.name}
                      </SelectItem>
                    ))}
                  </SelectContent>
                </Select>
              </div>
              <div className="space-y-2">
                <Label>Type</Label>
                <Select value={locationType} onValueChange={setLocationType}>
                  <SelectTrigger>
                    <SelectValue placeholder="All types" />
                  </SelectTrigger>
                  <SelectContent>
                    <SelectItem value="all">All types</SelectItem>
                    <SelectItem value="HUB">Hub</SelectItem>
                    <SelectItem value="SPOKE">Spoke</SelectItem>
                    <SelectItem value="MACHINE">Machine</SelectItem>
                  </SelectContent>
                </Select>
              </div>
              <div className="space-y-2">
                <Label>Category</Label>
                <Select value={categoryId} onValueChange={setCategoryId}>
                  <SelectTrigger>
                    <SelectValue placeholder="All categories" />
                  </SelectTrigger>
                  <SelectContent>
                    <SelectItem value="all">All categories</SelectItem>
                    {categories.map((cat) => (
                      <SelectItem key={cat.id} value={cat.id}>
                        {cat.name}
                      </SelectItem>
                    ))}
                  </SelectContent>
                </Select>
              </div>
              <div className="space-y-2">
                <Label>Brand</Label>
                <Select value={brandId} onValueChange={setBrandId}>
                  <SelectTrigger>
                    <SelectValue placeholder="All brands" />
                  </SelectTrigger>
                  <SelectContent>
                    <SelectItem value="all">All brands</SelectItem>
                    {brands.map((brand) => (
                      <SelectItem key={brand.id} value={brand.id}>
                        {brand.name}
                      </SelectItem>
                    ))}
                  </SelectContent>
                </Select>
              </div>
            </div>
          </CardContent>
        </Card>

        <MetricGrid className="mb-6">
          <SummaryMetricCard
            label="Warehouse"
            value={
              report
                ? summaryValue(report.summary.warehouseCostValue, report.summary.warehouseRetailValue)
                : '-'
            }
            icon={Warehouse}
            isLoading={loading}
          />
          <SummaryMetricCard
            label="In machines"
            value={
              report
                ? summaryValue(report.summary.machinesCostValue, report.summary.machinesRetailValue)
                : '-'
            }
            icon={Cpu}
            isLoading={loading}
          />
          <SummaryMetricCard
            label="Total estate"
            value={
              report
                ? summaryValue(report.summary.totalCostValue, report.summary.totalRetailValue)
                : '-'
            }
            icon={PoundSterling}
            isLoading={loading}
          />
          <SummaryMetricCard
            label="Total units"
            value={report ? report.summary.totalUnits.toLocaleString() : '-'}
            icon={Layers}
            isLoading={loading}
          />
        </MetricGrid>

        <Card>
          <Table>
            <TableHeader>
              <TableRow>
                <TableHead>Location</TableHead>
                <TableHead>Type</TableHead>
                <TableHead className="text-right">SKUs</TableHead>
                <TableHead className="text-right">Units</TableHead>
                <TableHead className="text-right">Cost value</TableHead>
                <TableHead className="text-right">Retail value</TableHead>
              </TableRow>
            </TableHeader>
            <TableBody>
              {loading ? (
                <TableRow>
                  <TableCell colSpan={6} className="text-center py-8 text-muted-foreground">
                    Loading...
                  </TableCell>
                </TableRow>
              ) : report?.items.length === 0 ? (
                <TableRow>
                  <TableCell colSpan={6} className="text-center py-8 text-muted-foreground">
                    No stock data. Complete product pricing and run initial stock take.
                  </TableCell>
                </TableRow>
              ) : (
                report?.items.map((item) => (
                  <TableRow key={`${item.locationType}-${item.locationId}`}>
                    <TableCell>
                      <LocationLink
                        locationType={item.locationType}
                        locationId={item.locationId}
                        name={item.locationName}
                      />
                    </TableCell>
                    <TableCell>
                      <span
                        className={cn(
                          'inline-flex rounded-full px-2 py-0.5 text-xs font-medium',
                          item.locationType === 'HUB' && 'bg-blue-100 text-blue-800',
                          item.locationType === 'SPOKE' && 'bg-purple-100 text-purple-800',
                          item.locationType === 'MACHINE' && 'bg-gray-100 text-gray-800'
                        )}
                      >
                        {item.locationType}
                      </span>
                    </TableCell>
                    <TableCell className="text-right">{item.productCount}</TableCell>
                    <TableCell className="text-right">{item.totalUnits.toLocaleString()}</TableCell>
                    <TableCell
                      className={cn(
                        'text-right font-medium',
                        view === 'cost' && 'text-foreground'
                      )}
                    >
                      {formatCurrency(item.costValue)}
                    </TableCell>
                    <TableCell
                      className={cn(
                        'text-right font-medium',
                        view === 'retail' && 'text-foreground'
                      )}
                    >
                      {formatCurrency(item.retailValue)}
                    </TableCell>
                  </TableRow>
                ))
              )}
            </TableBody>
          </Table>
        </Card>
      </div>
    </div>
  );
}
